import React, { useState } from "react";

export interface KPICardProps {
  title: string;
  value: string;
  icon: React.ElementType;
  iconBg?: string;       // e.g. "bg-indigo-50"
  iconColor?: string;    // e.g. "text-indigo-500"
  badge?: string;        // e.g. "+12.4%" or "Budget Cap"
  badgeColor?: string;   // "green" | "gray" — defaults to green
  barGradient?: string;  // e.g. "from-indigo-500 via-blue-500 to-blue-700"
  color?: string;        // legacy, kept for compatibility
  info?: string;         // tooltip text for the ⓘ button
}

export const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon: Icon,
  iconBg = "bg-indigo-50",
  iconColor = "text-indigo-500",
  badge,
  badgeColor = "green",
  barGradient = "from-indigo-500 to-blue-600",
  info,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const badgeStyle =
    badgeColor === "gray"
      ? "text-gray-500 font-medium text-xs bg-gray-100 px-3 py-1 rounded-full"
      : "text-emerald-600 font-semibold text-xs bg-emerald-50 px-3 py-1 rounded-full";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col justify-between min-h-[140px] relative overflow-visible transition-all duration-300 hover:shadow-md hover:-translate-y-1">
      {/* Top row: icon + badge + info */}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-[35%] ${iconBg} flex items-center justify-center shrink-0`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>

        {/* Right side: badge + info button */}
        <div className="flex items-center gap-2">
          {badge && (
            <span className={badgeStyle}>{badge}</span>
          )}

          {/* Info icon with hover tooltip */}
          {info && (
            <div 
              className="relative"
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
            >
              <div
                className="w-5 h-5 rounded-full border border-gray-300 text-gray-400 hover:text-gray-600 hover:border-gray-400 flex items-center justify-center transition-colors shrink-0"
                aria-label="More information"
              >
                <span className="text-[10px] font-bold leading-none select-none">i</span>
              </div>

              {/* Tooltip */}
              {tooltipOpen && (
                <div
                  className="absolute right-0 top-7 z-50 w-52 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 shadow-xl leading-relaxed"
                >
                  {/* Arrow */}
                  <div className="absolute -top-1.5 right-2 w-3 h-3 bg-gray-900 rotate-45 rounded-sm" />
                  <span className="relative z-10">{info}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Label + Value */}
      <div className="flex-1">
        <p className="text-sm text-[#464554] font-medium mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900 leading-tight">{value}</p>
      </div>

      {/* Bottom gradient bar */}
      <div className="mt-4">
        <div className={`h-1 rounded-full bg-gradient-to-r ${barGradient}`} style={{ width: "75%" }} />
      </div>
    </div>
  );
};

export default KPICard;
